import React, { useEffect } from "react";
import type { WorkflowData } from '@/lib/types';
import { useParams, Link, useLocation } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Zap, Code, FileText, PlayCircle, ArrowRight, Star, Users, Clock, Heart, Download, CheckCircle, MessageCircle, Bot, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getComplexityColor } from "@/lib/data";
import { workflowGalleryService } from "@/lib/api/services";
import { useApi } from "@/hooks/useApi";
import { PageSkeleton, GenericError } from "@/components/common";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CommentSection } from "@/components/common/CommentSection";
import { NotFoundError } from "@/components/common";

// Mock data for related workflows
const mockRelatedWorkflows = [
  {
    id: "related-1",
    title: "Advanced Document Processing",
    description: "Enhanced document analysis with OCR and entity extraction capabilities.",
    author: { name: "Alex Johnson", avatar: "/placeholder.svg" },
    rating: 4.7,
    uses: 892,
    image: "/placeholder.svg",
    category: "Document Processing"
  },
  {
    id: "related-2", 
    title: "Multi-Language Text Analysis",
    description: "Comprehensive text analysis supporting 50+ languages with sentiment detection.",
    author: { name: "Maria Rodriguez", avatar: "/placeholder.svg" },
    rating: 4.5,
    uses: 654,
    image: "/placeholder.svg",
    category: "Text Processing"
  },
  {
    id: "related-3",
    title: "Real-time Data Pipeline",
    description: "Stream processing workflow for real-time data analysis and alerting.",
    author: { name: "David Kim", avatar: "/placeholder.svg" },
    rating: 4.9,
    uses: 1205,
    image: "/placeholder.svg",
    category: "Data Processing"
  }
];

export default function WorkflowDetailPage() {
  const { workflowId } = useParams();
  const location = useLocation();
  
  // Fetch workflow by ID from API
  const workflowResult = useApi(
    () => workflowGalleryService.getById(workflowId || ""), 
    [workflowId], 
    { showErrorToast: true }
  );

  // Handle hash navigation to comments section
  useEffect(() => {
    if (location.hash === '#comments') {
      const commentsSection = document.getElementById('comments-section');
      if (commentsSection) {
        commentsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  if (workflowResult.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Header currentPage="workflows" variant="gallery" />
        <PageSkeleton />
        <Footer />
      </div>
    );
  }

  if (workflowResult.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Header currentPage="workflows" variant="gallery" />
        <div className="container mx-auto px-4 py-16">
          <GenericError onRetry={() => workflowResult.refetch()} />
        </div>
        <Footer />
      </div>
    );
  }

  const workflow = workflowResult.data;

  if (!workflow) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Header currentPage="workflows" variant="gallery" />
        <div className="container mx-auto px-4 py-16">
          <NotFoundError 
            title="Workflow Not Found"
            description="The workflow you're looking for doesn't exist or has been removed."
            onGoBack={() => window.history.back()}
            className="max-w-md mx-auto"
          />
        </div>
        <Footer />
      </div>
    );
  }

  // Map category to icon since backend doesn't store React components
  const getIconForCategory = (category: string) => {
    switch (category) {
      case 'Document Processing': return FileText;
      case 'Data Processing': return Database;
      case 'AI/ML': return Bot;
      case 'Productivity': return Zap;
      default: return Zap;
    }
  };
  
  const IconComponent = workflow.icon || getIconForCategory(workflow.category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header currentPage="workflows" variant="gallery" />
      
      {/* Hero Section */}
      <div className="bg-white dark:bg-slate-800 border-b">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <Link to="/gallery/workflows">
              <Button variant="ghost" className="mb-6 text-blue-600 hover:text-blue-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Workflow Gallery
              </Button>
            </Link>

            <div className="flex flex-col md:flex-row items-start md:justify-between mb-8">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                  <IconComponent className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-3">{workflow.title}</h1>
                  <p className="text-xl text-slate-600 dark:text-slate-300 mb-4 max-w-2xl">{workflow.description}</p>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className={getComplexityColor(workflow.complexity)}>
                      {workflow.complexity}
                    </Badge>
                    <Badge variant="outline">{workflow.category}</Badge>
                    <div className="flex items-center space-x-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium">{workflow.rating}</span>
                    </div>
                  </div>

                  {/* Author info */}
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={workflow.author.avatar || "/placeholder.svg"} alt={workflow.author.name} />
                      <AvatarFallback className="text-xs">
                        {workflow.author.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">by {workflow.author.name}</span>
                      {workflow.author.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 md:mt-0">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Copy className="h-4 w-4 mr-2" />
                  Clone Workflow
                </Button>
                <Button variant="outline">
                  <Heart className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Workflow Canvas Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <PlayCircle className="h-8 w-8 mr-3 text-blue-600" />
              Workflow Canvas
            </h2>
            
            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="w-full h-96 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-slate-200 dark:bg-slate-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-10 h-10 text-slate-500" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-lg">Interactive Workflow Visualization</p>
                    <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">
                      Full workflow canvas with {workflow.nodes} connected nodes would appear here
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* Stats & Details Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <Code className="h-8 w-8 mr-3 text-blue-600" />
              Details & Statistics
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Workflow Stats */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Workflow Statistics</CardTitle>
                  <CardDescription>Usage and performance metrics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <MessageCircle className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{workflow.comments?.length || 0}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Comments</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {workflow.uses.toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Total Uses</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Category</span>
                      <span className="text-sm text-slate-600 dark:text-slate-400">{workflow.category}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Complexity</span>
                      <Badge className={getComplexityColor(workflow.complexity)} variant="secondary">
                        {workflow.complexity}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Rating</span>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{workflow.rating}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tags & Technologies */}
              <Card className="bg-white dark:bg-slate-800">
                <CardHeader>
                  <CardTitle className="text-xl text-slate-900 dark:text-white">Tags & Technologies</CardTitle>
                  <CardDescription>Technologies and use cases covered</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-3">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {workflow.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-sm">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900 dark:text-white">Quick Actions</h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Workflow URL
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <Download className="h-4 w-4 mr-2" />
                        Export Configuration
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open in Editor
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* Documentation Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <FileText className="h-8 w-8 mr-3 text-blue-600" />
              Documentation
            </h2>
            
            <Card className="bg-white dark:bg-slate-800">
              <CardContent className="p-8">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <h3>Overview</h3>
                  <p>
                    The {workflow.title} workflow is designed to {workflow.description.toLowerCase()}. 
                    This comprehensive automation solution combines multiple AI models and processing steps 
                    to deliver reliable and scalable results.
                  </p>
                  
                  <h3>Key Features</h3>
                  <ul>
                    <li><strong>Multi-step Processing:</strong> Orchestrates {workflow.nodes} interconnected nodes for comprehensive analysis</li>
                    <li><strong>Scalable Architecture:</strong> Handles both batch and real-time processing requirements</li>
                    <li><strong>Quality Assurance:</strong> Built-in validation and error handling mechanisms</li>
                    <li><strong>Integration Ready:</strong> Compatible with popular data sources and destinations</li>
                  </ul>

                  <h3>Use Cases</h3>
                  <p>
                    This workflow is particularly effective for:
                  </p>
                  <ul>
                    <li>Large-scale content processing and analysis</li>
                    <li>Automated quality control and validation</li>
                    <li>Multi-modal data transformation pipelines</li>
                    <li>Real-time monitoring and alerting systems</li>
                  </ul>

                  <h3>Performance & Limitations</h3>
                  <p>
                    Based on community usage data from {workflow.uses.toLocaleString()} deployments:
                  </p>
                  <ul>
                    <li>Average processing time: 2-5 minutes per batch</li>
                    <li>Recommended for datasets up to 10GB</li>
                    <li>Requires minimum 4GB RAM for optimal performance</li>
                    <li>Works best with structured and semi-structured data</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* Comments Section */}
          <CommentSection
            itemId={String(workflow.id)}
            itemType="workflow"
            accentColor="blue"
            comments={workflow.comments || []}
            onAddComment={(comment) => {
              console.log('New comment:', comment);
              // In real app, this would call an API
            }}
          />

          <Separator />

          {/* Related Workflows Section */}
          <section>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 flex items-center">
              <ArrowRight className="h-8 w-8 mr-3 text-blue-600" />
              Related Workflows
            </h2>
            
            <div className="grid md:grid-cols-1 gap-6 mb-8">
              {mockRelatedWorkflows.map((relatedWorkflow) => (
                <Card key={relatedWorkflow.id} className="bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      <img
                        src={relatedWorkflow.image}
                        alt={`${relatedWorkflow.title} preview`}
                        className="w-24 h-16 object-cover rounded-lg bg-slate-100 dark:bg-slate-700"
                      />
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                          <Link 
                            to={`/workflow/${relatedWorkflow.id}`}
                            className="hover:text-blue-600 hover:underline"
                          >
                            {relatedWorkflow.title}
                          </Link>
                        </h3>
                        <p className="text-slate-600 dark:text-slate-300 mb-3">{relatedWorkflow.description}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                          <span>by {relatedWorkflow.author.name}</span>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{relatedWorkflow.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            <span>{relatedWorkflow.uses} uses</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        View Workflow
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Link to="/gallery/workflows">
                <Button variant="outline" size="lg" className="bg-blue-600 text-white hover:bg-blue-700">
                  <PlayCircle className="h-5 w-5 mr-2" />
                  Browse All Workflows
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
            </div>
          </section>

        </div>
      </div>
      
      <Footer />
    </div>
  );
}